/**
 * @Description This is FasatGridBar container page
 * @FileName fasatGridBar.js
 * @Author SAMAPTI TALUKDAR-talusam
 * @CreatedOn 25 January, 2021 18:32:32
 * @IssueID CDTFASATMOD-502
 */
import { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import '../../../../index.scss';
import './fasatGridBar.scss';
import { globalConstants, staticCommonLabelKeys } from '../../moduleConstants';
import { getCurrentLocale } from '../../i18n';
import FasatCheckbox from '../fasatCheckbox/fasatCheckbox';
import UserContext from '../../../../userContext';
import {
  privateClickHandler,
  configPrivate,
  setDelRefEnbl,
  setDelRefLbl,
  onClickExport,
  showEditActionBtn,
  showAddActionBtn,
  showCloneActionBtn,
  showDeleteActionBtn,
  showImportActionBtn,
  showExportActionBtn,
  showChangeStatusActionBtn,
  showSelectLetterActionBtn,
  showPageSection,
  checkOperation,
  ButtonOperation,
  ApprovePaymentBtn,
  ActivityProcessBtn,
  TerminatedRecordButton,
} from './fasatGridBarHelpers';

const privateCheckboxClass = 'privateCheckbox';
const { external } = globalConstants;
const externalUser = external;

const SetApproveEnabled = (cpStore) => {
  const enableApproveState = useSelector((state) => state[cpStore]?.enableApprove);
  return cpStore ? enableApproveState : false;
};

const GetStateSelector = (defaultEntityFilterConfig, entityFilterConfig) => {
  const listSortOptsState = useSelector((state) => entityFilterConfig && state[entityFilterConfig.REDUX_STATE]?.listSortOptions);
  const listSortOptions = defaultEntityFilterConfig?.listSortOptions || listSortOptsState;
  const listParamOptsState = useSelector((state) => entityFilterConfig && state[entityFilterConfig.REDUX_STATE]?.listParamOptions);
  const listParamOptions = defaultEntityFilterConfig?.listParamOptions || listParamOptsState;
  const listColumnOrderState = useSelector((state) => entityFilterConfig && state[entityFilterConfig.REDUX_STATE]?.listColumnOrder);
  const listColumnOrder = defaultEntityFilterConfig?.listColumnOrder || listColumnOrderState;
  const listGroupByState = useSelector((state) => entityFilterConfig && state[entityFilterConfig.REDUX_STATE]?.listGroupBy);
  const listGroupBy = defaultEntityFilterConfig?.listGroupBy || listGroupByState;
  const isDistinctActiveState = useSelector((state) => entityFilterConfig && state[entityFilterConfig.REDUX_STATE]?.isDistinctActive);
  const isDistinctActive = defaultEntityFilterConfig?.isDistinctActive || isDistinctActiveState;
  const selectedFilterState = useSelector((state) => entityFilterConfig && state[entityFilterConfig.REDUX_STATE]?.selectedFilter);
  const selectedFilter = defaultEntityFilterConfig?.selectedFilter || selectedFilterState;
  const processingInProgress = useSelector(
    ({ ApplicationStateFilterList }) => ApplicationStateFilterList.processingInProgress,
  );
  return { listSortOptions,
    listParamOptions,
    listColumnOrder,
    listGroupBy,
    isDistinctActive,
    selectedFilter,
    processingInProgress,
  };
};

function ActionSection({ t, showPrivateCheckbox, userType, isPrivate, setIsPrivate, privateHandler }) {
  const onHandleChange = useCallback((value) => privateClickHandler(value, setIsPrivate, privateHandler), [setIsPrivate, privateHandler]);

  if (showPrivateCheckbox && (userType !== externalUser)) {
    return (
      <div className={privateCheckboxClass}>
        <FasatCheckbox
          id="isPrivate"
          name="isPrivate"
          labelText={t(staticCommonLabelKeys.COMMON_LABEL_SHOW_PRIVATE)}
          onHandleChange={onHandleChange}
          defaultChecked={isPrivate}
        />
      </div>
    );
  }
  return (<></>);
}

function FasatGridBar({ addAction, exportAction, resetAction, resetEnabled, addBtnAriaLabel, cloneAction,
  onClickReset, onClickApprove, pageSize, onChangePageSize, pageSizeOptions, showDefaultChip, bulkAction,
  showPrivateCheckbox, approveAction, defaultChipText, activateExportButton, entityFilterConfig, onClickDelete,
  resetLabel, addAclKey, exportAclKey, privateHandler, resetButtonClick, onClickAdd, deleteAction, deleteEnabled,
  sharedFilter, relationshipParameter, relationshipSortOrder, exportFileName, approveLabel, deleteLabel,
  importAction, importEnabled, importLabel, onClickImport, listActive, showPageSize, cpStore, deleteAclKey,
  importAclKey, approveAclKey, remitAclKey, editAclKey, onClickEdit, editAction, pdDisableAdd, activateBulkButton,
  resetAriaLabel, reverseAction, reprocessAction, reverseAclKey, onClickReverse, reprocessAclKey, onClickReprocess,
  activateReprocessButton, ffExport, processNowAction, onClickProcessNow, defaultEntityFilterConfig,
  processNowAclKey, activateAUPButton, changeStatusAction, changeStatusAclKey, onClickChangeStatus,
  changeStatusLabel, changeStatusEnabled, selectLetterAclKey, onClickSelectLetter, selectLetterLabel,
  selectLetterEnabled, selectLetterAction, cancelAclKey, onClickCancel, onClickRemit, remitLabel, onClickBulk,
  approvePayment, downloadBtn, printBtn, viewBtn, externalExport, emitExport, quickSearchParam, isQuickSearchFilter,
  terminatedRecordBtnParams, backBtnParams, isExtrnalUser, pageSizeID, exportBtnID, formRequest, currentPage,
}) {
  const rbacData = useSelector(({ ApplicationStateRBAC, CodeAndHierarchy }) => (isExtrnalUser ? CodeAndHierarchy?.levelRBAC : ApplicationStateRBAC.rbacData));
  const [isPrivate, setIsPrivate] = useState(false);
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const locale = getCurrentLocale();
  const userType = useContext(UserContext);
  const { listSortOptions, listParamOptions, listColumnOrder, listGroupBy, isDistinctActive, selectedFilter,
    processingInProgress } = GetStateSelector(defaultEntityFilterConfig, entityFilterConfig);
  /**
    * Format Role list for dropdown
    */
  useEffect(() => {
    if (!resetButtonClick && isPrivate) {
      setIsPrivate(false);
      document.getElementById('isPrivate').click();
    }
  }, [resetButtonClick]);
  /**
    * Check and Disable Private Field
    */
  const onClickExportAction = useCallback(() => {
    if (!ffExport) {
      onClickExport({
        entityFilterConfig,
        listSortOptions,
        selectedFilter,
        listColumnOrder,
        relationshipParameter,
        listParamOptions,
        listGroupBy,
        relationshipSortOrder,
        locale,
        exportFileName,
        dispatch,
        processingInProgress,
        listActive,
        isDistinctActive,
        emitExport,
        externalExport,
        quickSearchParam,
        isQuickSearchFilter,
        pageSize,
        formRequest,
        currentPage,
      });
    } else {
      ffExport();
    }
  }, [onClickExport, entityFilterConfig, listSortOptions, selectedFilter,
    listColumnOrder, relationshipParameter, listParamOptions, listGroupBy,
    relationshipSortOrder, locale, exportFileName, dispatch, processingInProgress,
    listActive, isDistinctActive, emitExport, externalExport, quickSearchParam,
    isQuickSearchFilter, pageSize, formRequest, currentPage]);
  useEffect(() => {
    configPrivate(sharedFilter, isPrivate, setIsPrivate);
  }, [sharedFilter]);
  const approveEnabled = SetApproveEnabled(cpStore);
  const chkOptPrms = { approveAclKey, approveAction, approveEnabled, approveLabel, onClickApprove, defaultChipText };
  const buttonParams = { downloadBtn, printBtn, rbacData, viewBtn };
  const enableDeleteState = useSelector((state) => state[cpStore]?.enableDelete);
  const deleteRefEnabled = setDelRefEnbl(deleteEnabled, cpStore, enableDeleteState);
  const deleteRefLabel = setDelRefLbl(deleteLabel, t);
  const paymentBtnParams = { approvePayment, approveAclKey, approveEnabled, approveLabel, onClickApprove, rbacData, remitAclKey, remitLabel, onClickRemit, onClickCancel, t, cancelAclKey };
  const activityProcessBtnParams = { reverseAction, reprocessAction, processNowAction, reverseAclKey, activateReprocessButton, rbacData, onClickReverse, t, reprocessAclKey, onClickReprocess, processNowAclKey, activateAUPButton, onClickProcessNow, onClickBulk, bulkAction, activateBulkButton };
  return (
    <div className="fasatGridBar" data-testid="FasatGridBarComponent">
      {showPageSection({ showPageSize, t, pageSizeOptions, onChangePageSize, pageSize, pageSizeID, backBtnParams })}
      <div className="actionSection">
        <ActionSection
          t={t}
          showPrivateCheckbox={showPrivateCheckbox}
          userType={userType}
          isPrivate={isPrivate}
          setIsPrivate={setIsPrivate}
          privateHandler={privateHandler}
        />
        {checkOperation({ ...chkOptPrms, resetAction, showDefaultChip, resetEnabled, onClickReset, resetLabel, resetAriaLabel, rbacData })}
        {terminatedRecordBtnParams && TerminatedRecordButton(terminatedRecordBtnParams, t)}
        {ButtonOperation({ ...buttonParams })}
        {ApprovePaymentBtn({ ...paymentBtnParams })}
        {ActivityProcessBtn({ ...activityProcessBtnParams })}
        {editAction && (showEditActionBtn(editAclKey, rbacData, onClickEdit, t))}
        {addAction && (showAddActionBtn(addAclKey, rbacData, onClickAdd, t, addBtnAriaLabel, pdDisableAdd))}
        {cloneAction && (showCloneActionBtn(addAclKey, rbacData, onClickAdd, t, addBtnAriaLabel, pdDisableAdd))}
        {deleteAction && (showDeleteActionBtn(deleteAclKey, deleteRefEnabled, deleteRefLabel, onClickDelete, rbacData))}
        {importAction && (showImportActionBtn(importAclKey, importEnabled, importLabel, onClickImport, rbacData))}
        {changeStatusAction && (showChangeStatusActionBtn(
          t,
          changeStatusAclKey,
          onClickChangeStatus,
          changeStatusLabel,
          changeStatusEnabled,
          rbacData,
        ))}
        { selectLetterAction && (showSelectLetterActionBtn(
          t,
          selectLetterAclKey,
          onClickSelectLetter,
          selectLetterLabel,
          selectLetterEnabled,
          rbacData,
        ))}
        {exportAction && (
          showExportActionBtn({ exportAclKey,
            rbacData,
            activateExportButton,
            t,
            entityFilterConfig,
            listSortOptions,
            selectedFilter,
            listColumnOrder,
            listParamOptions,
            listGroupBy,
            relationshipSortOrder,
            locale,
            exportFileName,
            dispatch,
            onClickExportAction,
            relationshipParameter,
            processingInProgress,
            listActive,
            isDistinctActive,
            ffExport,
            emitExport,
            externalExport,
            quickSearchParam,
            isQuickSearchFilter,
            pageSize,
            exportBtnID,
            formRequest,
            currentPage })
        )}
      </div>
    </div>
  );
}

FasatGridBar.propTypes = {
  selectLetterAction: PropTypes.bool,
  changeStatusAction: PropTypes.bool,
  editAction: PropTypes.bool,
  addAction: PropTypes.bool,
  cloneAction: PropTypes.bool,
  exportAction: PropTypes.bool,
  resetAction: PropTypes.bool,
  resetEnabled: PropTypes.bool,
  showDefaultChip: PropTypes.bool,
  showPrivateCheckbox: PropTypes.bool,
  approveAction: PropTypes.bool,
  approvePayment: PropTypes.bool,
  deleteEnabled: PropTypes.bool,
  deleteAction: PropTypes.bool,
  showPageSize: PropTypes.bool,
  defaultChipText: PropTypes.string,
  approveLabel: PropTypes.string,
  deleteLabel: PropTypes.string,
  pageSize: PropTypes.number,
  onClickAdd: PropTypes.func,
  onClickReset: PropTypes.func,
  onClickApprove: PropTypes.func,
  onClickRemit: PropTypes.func,
  onClickDelete: PropTypes.func,
  onChangePageSize: PropTypes.func,
  emitExport: PropTypes.func,
  quickSearchParam: PropTypes.shape(),
  isQuickSearchFilter: PropTypes.string,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  })),
  activateExportButton: PropTypes.bool,
  resetLabel: PropTypes.string,
  resetAriaLabel: PropTypes.string,
  exportFileName: PropTypes.string,
  addBtnAriaLabel: PropTypes.string,
  cpStore: PropTypes.string, // Required for enabling approve and delete button
  pageSizeID: PropTypes.string,
  exportBtnID: PropTypes.string,
  formRequest: PropTypes.shape({}),
  currentPage: PropTypes.number,
};

FasatGridBar.defaultProps = {
  selectLetterAction: false,
  changeStatusAction: false,
  editAction: false,
  cloneAction: false,
  addAction: true,
  exportAction: true,
  resetAction: false,
  resetEnabled: true,
  showDefaultChip: false,
  showPrivateCheckbox: false,
  activateExportButton: false,
  approveAction: false,
  approvePayment: false,
  deleteAction: false,
  deleteEnabled: false,
  showPageSize: true,
  approveLabel: '',
  deleteLabel: '',
  pageSize: 0,
  defaultChipText: 'My default',
  onClickAdd: undefined,
  onClickReset: () => undefined,
  onClickApprove: () => undefined,
  onClickDelete: () => undefined,
  // pageSize: 10,
  onChangePageSize: () => undefined,
  onClickRemit: () => undefined,
  emitExport: () => undefined,
  quickSearchParam: [],
  isQuickSearchFilter: '',
  resetLabel: null,
  resetAriaLabel: null,
  exportFileName: null,
  pageSizeOptions: [
    {
      label: '10',
      value: '10',
    },
    {
      label: '20',
      value: '20',
    },
    {
      label: '50',
      value: '50',
    },
    {
      label: '100',
      value: '100',
    },
  ],
  addBtnAriaLabel: null,
  cpStore: null,
  pageSizeID: 'pageSize',
  exportBtnID: 'exportListButton',
  formRequest: {},
  currentPage: 1,
};

export default FasatGridBar;
